/**
 * IntegrationConfigDialog Component
 * 
 * Dialog for managing company product integrations
 * Supports multiple product types with instance-based configuration
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import type { Company, ProductInstance } from '@/features/multi-company/types/company.types';
import { AddProductDialog } from './AddProductDialog';
import { ProductInstancesList } from './ProductInstancesList';

interface IntegrationConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  onSave: (company: Company) => void;
}

export function IntegrationConfigDialog({
  open,
  onOpenChange,
  company,
  onSave,
}: IntegrationConfigDialogProps) {
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);

  // Update editingCompany when company prop changes
  useEffect(() => {
    if (company) {
      setEditingCompany(company);
    }
  }, [company]);

  const handleAddProduct = (newInstances: ProductInstance[]) => {
    if (!editingCompany) return;

    setEditingCompany({
      ...editingCompany,
      productInstances: [
        ...editingCompany.productInstances,
        ...newInstances,
      ],
    });
  };

  const handleUpdateProduct = (updatedInstance: ProductInstance) => {
    if (!editingCompany) return;

    setEditingCompany({
      ...editingCompany,
      productInstances: editingCompany.productInstances.map((instance) =>
        instance.id === updatedInstance.id ? updatedInstance : instance
      ),
    });
  };

  const handleDeleteProduct = (instanceId: string) => {
    if (!editingCompany) return;

    setEditingCompany({
      ...editingCompany,
      productInstances: editingCompany.productInstances.filter(
        (instance) => instance.id !== instanceId && instance.autoConnectedBy !== instanceId
      ),
    });
  };

  const handleSave = () => {
    if (editingCompany) {
      onSave(editingCompany);
    }
    onOpenChange(false);
  };

  if (!company || !editingCompany) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Integration Configuration</DialogTitle>
            <DialogDescription>
              Configure product integrations for {company.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-medium">Configured Products</h3>
              <Button onClick={() => setShowAddProduct(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </div>

            <ProductInstancesList
              instances={editingCompany.productInstances}
              onUpdate={handleUpdateProduct}
              onDelete={handleDeleteProduct}
            />

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                Save Configuration
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AddProductDialog
        open={showAddProduct}
        onOpenChange={setShowAddProduct}
        companyId={company.id}
        companyCode={company.code}
        existingInstances={editingCompany.productInstances}
        onAdd={handleAddProduct}
      />
    </>
  );
}
